import { useAuth } from 'react-oidc-context';
import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router';

import AppLayout from 'layout/AppLayout';
import PageFallback from 'components/PageFallback';
import jwtDecode from 'jwt-decode';
import { AccountService, CarrierService } from 'services';
import { EFeatureAvailability } from 'common/carrier.interface';
import { aggregateFinancierMenuState } from 'utils/financier-menu';
import { isUserAdmin } from 'utils/auth';

export const AuthenticatedArea = () => {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      sessionStorage.setItem('returnTo', window.location.pathname + window.location.search);
      navigate('/login');
    }
  }, [auth.isAuthenticated, auth.isLoading, navigate]);

  if (auth.isLoading) return <PageFallback />;

  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
};

export const PrivateRoute = ({ permittedRoles }: any) => {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isLoading || !auth.user) return;

    const decodedToken: any = jwtDecode(auth.user.access_token);
    const roles = decodedToken.roles || [];
    const removedRoles = ['default-roles-torqi', 'uma_authorization', 'offline_access'];
    const filteredRoles = roles.filter((role: string) => !removedRoles.includes(role));

    if (!permittedRoles.some((role: string) => filteredRoles.includes(role))) {
      navigate('/contratar', { replace: true });
    }
  }, [auth.isLoading, auth.user, navigate]);

  return <Outlet />;
};

type FeatureKey = 'anticipation' | 'guarantee' | 'reports';

const featureStateKey: Record<FeatureKey, string> = {
  anticipation: 'anticipationMenuState',
  guarantee: 'guaranteeMenuState',
  reports: 'reportsMenuState',
};

const featureRouteKey: Record<FeatureKey, string> = {
  anticipation: '/contratar',
  guarantee: '/garantia',
  reports: '/relatorios',
};

export const FeatureRoute = ({ featureKey }: { featureKey: FeatureKey }) => {
  const navigate = useNavigate();
  const isAdmin = isUserAdmin();
  const [ready, setReady] = useState(isAdmin);

  useEffect(() => {
    if (isAdmin) return;

    const checkFeature = async () => {
      let redirecting = false;

      try {
        const accountService = new AccountService();
        const carrierService = new CarrierService();
        const userInfo = await accountService.getMe();

        if (!userInfo.carrierRootId) {
          redirecting = true;
          navigate('/bem-vindo', { replace: true });
          return;
        }

        const carrierRoot = await carrierService.getCarrierRoot(userInfo.carrierRootId);
        const stateKey = featureStateKey[featureKey];

        if (carrierRoot[stateKey] !== EFeatureAvailability.ENABLED) {
          const firstEnabledFeature = (Object.keys(featureStateKey) as FeatureKey[]).find(
            (key) => carrierRoot[featureStateKey[key]] === EFeatureAvailability.ENABLED
          );

          redirecting = true;

          navigate(firstEnabledFeature ? featureRouteKey[firstEnabledFeature] : '/bem-vindo', { replace: true });

          return;
        }
      } catch {
        // fail open
      } finally {
        if (!redirecting) setReady(true);
      }
    };

    checkFeature();
  }, [featureKey, isAdmin, navigate]);

  if (!ready) return null;

  return <Outlet />;
};

type FinancierFeatureKey = 'arrecadacao' | 'gestaoLimite' | 'reports' | 'file';

const financierStateKey: Record<
  FinancierFeatureKey,
  'arrecadacaoMenuState' | 'gestaoLimiteMenuState' | 'reportsMenuState' | 'fileMenuState'
> = {
  arrecadacao: 'arrecadacaoMenuState',
  gestaoLimite: 'gestaoLimiteMenuState',
  reports: 'reportsMenuState',
  file: 'fileMenuState',
};

const financierRouteKey: Record<FinancierFeatureKey, string> = {
  arrecadacao: '/financier/arrecadacao/vencimento',
  gestaoLimite: '/financier/gestao-limite',
  reports: '/financier/relatorios/painel-garantia',
  file: '/financier/documentacao/gerenciamento',
};

export const FinancierFeatureRoute = ({ featureKey }: { featureKey: FinancierFeatureKey }) => {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const checkFeature = async () => {
      let redirecting = false;
      try {
        const accountService = new AccountService();
        const userInfo = await accountService.getMe();
        const linkedFinanciers = userInfo.financiers || [];

        if (!linkedFinanciers.length) {
          redirecting = true;
          navigate('/bem-vindo', { replace: true });
          return;
        }

        const stateOf = (key: FinancierFeatureKey) =>
          aggregateFinancierMenuState(linkedFinanciers.map((item) => item[financierStateKey[key]]));
        const stateValue = stateOf(featureKey);

        if (stateValue === EFeatureAvailability.HIDDEN) {
          const firstAvailable = (Object.keys(financierStateKey) as FinancierFeatureKey[]).find(
            (key) => stateOf(key) !== EFeatureAvailability.HIDDEN
          );
          redirecting = true;
          navigate(firstAvailable ? financierRouteKey[firstAvailable] : '/bem-vindo', { replace: true });
          return;
        }
      } catch {
        // fail open
      } finally {
        if (!redirecting) setReady(true);
      }
    };

    checkFeature();
  }, [featureKey, navigate]);

  if (!ready) return null;
  return <Outlet />;
};
